/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {MenuItem} from "@mui/material";
import ArgosSelect from "../../../../../../components/Form/Select/ArgosSelect";
import {MRT_Cell, MRT_ColumnDef} from "material-react-table";
import {MAP_OBJECT_PROFILE_KEY_TO_PROFILE_NAME, ObjectPropertiesInput, objectPropertiesInput, ObjectPropertyRow} from "../../constants";
import {useEffect, useState} from "react";
import {toast} from "react-hot-toast";
import {useHasWriteAccess} from "../../../../../../../redux/ducks/AccessControl/accessControlHelpers";

import {ArgosTableSimple} from "../../../../../../components/Table/ArgosTableSimple";


interface IObjectPropertiesProps {
  temperatureArray: number[]
  handleTempChange: any
  baselineData: any
  isDialogOpen: any
  handleObjectCategoryChange: any
  objectData: any
  objectDataMap: any
  handleObjectNameChange: any
  processListOfVersions: any
  handleObjectVersionChange: any
  handleProfileChange: any
  gmid: string
  configIndex: any
  setRCS: any
  setSNR: any
  setAttenuation: any
  setIsPublicConfig: any
}

const ObjectProperties = (props: IObjectPropertiesProps) => {

  const [tableData, setTableData] = useState<any>([]);
  const [validationErrors, setValidationErrors] = useState<any>({});
  const hasWriteAccess = useHasWriteAccess();

  useEffect(() => {
    populateTable();
  }, [props])

  const populateTable = () => {
    let _tableData: ObjectPropertyRow[] = []
    let data = props?.baselineData;
    objectPropertiesInput.forEach((element: ObjectPropertiesInput) => {
      let _tableDataObject: ObjectPropertyRow = {
        objectPropertyName: element.label,
        unit: element.unit,
        objectPropertyValue: data?.[element.key],
        objectPropertyKey: element.key
      }
      _tableData.push(_tableDataObject)
    })
    setTableData(_tableData)
  }

  function helperSetValidationErrors(newVal: string, cell: any, message: string) {
    setValidationErrors({
      ...validationErrors,
      [cell.id]: message
    })

    toast.error(message,
      {
        style: {
          marginTop: "300px"
        },
        duration: 3000
      })
  }


  /**
   * Retrieves a list of distinct versions for a selected object from the object data map.
   *
   * @function
   * @returns {Array<string>} An array of distinct version strings for the selected object.
   * @throws {Error} Logs an error if `objectDataMap` or `selectedObjectName` is invalid.
   *
   * @param {Object} props - The input properties.
   * @param {Object} props.baselineData - Baseline data for the object.
   * @param {string} props.baselineData.objectName - The name of the currently selected object.
   * @param {Object[]} props.objectDataMap - The map containing all object data.
   *
   * @example
   * // Example usage:
   * const versions = versionsList({
   *     baselineData: { objectName: "ExampleObject" },
   *     objectDataMap: [ { type: "Potentially Moving Object", name: "ExampleObject", latestVersion: "v1.0" } ]
   * });
   * console.log(versions); // Output: ["v1.0"]
   */
  function versionsList() {
    const selectedObjectName = props?.baselineData?.objectName; // Get the currently selected object name

    // Check if objectDataMap and selectedObjectName are valid
    if (!props?.objectDataMap || !selectedObjectName) {
      console.error("objectDataMap or selectedObjectName is invalid");
      return [];
    }

    // Filter for the objects
    const filteredObjects = props?.objectDataMap.filter(
      (element: any) => (element.type === "Potentially Moving Object" || element.type === "Miscellaneous") && element.type === props?.baselineData.objectCategory
    );

    // Extract versions for the selected object name only
    const latestVersions = filteredObjects.flatMap((element: any) =>
      element.sub.flatMap((subArray: any[]) =>
        subArray
          .filter((item: any) => item.objectName === selectedObjectName) // Match the selected object name
          .map((item: any) => item.latestVersion)
      )
    );

    // Remove duplicates from the latestVersions array
    const distinctVersions = Array.from(new Set(latestVersions));
    return distinctVersions;
  }

  const validateInput = (newVal: string, cell: any) => {
    let newValnum = Number(newVal)
    if (isNaN(newValnum)) {
      helperSetValidationErrors(newVal, cell, `Not valid value, must be a number`)
      return false
    }
    return true
  }
  const getCommonEditTextFieldProps =
    (
      cell: MRT_Cell<ObjectPropertyRow>,
    ): MRT_ColumnDef<ObjectPropertyRow>['muiEditTextFieldProps'] => {
      return {
        error: !!validationErrors[cell.id],
        helperText: validationErrors[cell.id],
        onKeyDown: (e) => {
          if (e.code == "Tab") {
            e.stopPropagation()
            e.preventDefault()
          }
        },
        onBlur: (event) => {
          if (cell.row.original.objectPropertyKey == "snr" || cell.row.original.objectPropertyKey == `attenuation` || cell.row.original.objectPropertyKey == "rcs") {
            const isValid = validateInput(event.target.value, cell);
            if (isValid) {
              delete validationErrors[cell.id];
              if (cell.row.original.objectPropertyKey == "snr") {
                props?.setSNR(event.target.value)
              } else if (cell.row.original.objectPropertyKey == `attenuation`) {
                props?.setAttenuation(event.target.value)
              } else {
                props?.setRCS(event.target.value)
              }
            }
          }
        },
      };
    }

  const objectPropertiesRowColumns: MRT_ColumnDef<ObjectPropertyRow>[] = [
    {
      header: "Name",
      accessorKey: "objectPropertyName",
      enableEditing: false,
    },
    {
      header: "Value",
      accessorKey: "objectPropertyValue",
      enableEditing: true,
      minSize: 700,
      muiEditTextFieldProps: ({cell}) => ({
        ...getCommonEditTextFieldProps(cell),
        disabled: !hasWriteAccess ||
          (cell.row.original.objectPropertyKey == "objectVersion" && (props?.baselineData.objectName == "" || props?.baselineData.objectCategory == "Custom"))
          || (cell.row.original.objectPropertyKey == "rcs" && props?.baselineData.objectCategory != "Custom")
      }),
      Cell: ({cell}) => {
        let _objectPropertyKey: string = cell.row.original.objectPropertyKey
        let inputObject: any;
        objectPropertiesInput.forEach((input: ObjectPropertiesInput) => {
          if (input.key == _objectPropertyKey) {
            inputObject = input
          }
        })

        if (inputObject.isDropDown) {
          let dropdownValue: any;
          let menuItems: any = []
          switch (_objectPropertyKey) {
            case "objectCategory":
              dropdownValue = props?.baselineData?.objectCategory
              menuItems = props?.objectDataMap
                .filter((element: any) => element.type !== 'Custom')
                .map((element: any) => element.type);
              break;

            case "objectName":
              if (props?.baselineData?.objectCategory !== "Custom" && props?.baselineData?.objectCategory !== "Baseline") {
                const filteredObjects = Object.values(props?.objectDataMap || {}).filter(
                  (element: any) => (element.type === "Potentially Moving Object" || element.type === "Miscellaneous") && element.type === props?.baselineData.objectCategory
                );

                // Extract all object names from 'sub' array of arrays
                menuItems = filteredObjects.flatMap((element: any) =>
                  element.sub.flatMap((innerArray: any[]) =>
                    innerArray.map((item: any) => item.objectName)
                  )
                );
              } else if (props.baselineData?.objectCategory === 'Baseline') {
                menuItems = [props.baselineData.objectName]
              } else {
                menuItems = []
              }
              dropdownValue = props?.baselineData.objectName
              break;

            case "objectVersion":
              dropdownValue = props?.baselineData?.objectVersion
              if (props?.baselineData.objectName != "" && props?.baselineData.objectCategory != "Custom") {
                menuItems = versionsList()
              } else {
                menuItems = []
              }
              break;
            case "objectProperties":
              if (props?.baselineData.objectCategory == "Baseline") {
                dropdownValue = props?.baselineData?.objectVariant;
                menuItems = ["Front Profile", "Side Profile", "Rear Profile"]
              } else if (props?.baselineData.objectCategory != "Custom") {
                dropdownValue = props?.baselineData?.objectVariant;
                menuItems = ["Front Profile", "Side Profile", "Rear Profile"]
              } else {
                dropdownValue = "";
                menuItems = []
              }
              break;
            case "temperature":
              dropdownValue = props?.baselineData?.temperature
              menuItems = props?.temperatureArray
              break;
          }
          const isBaseline = props?.baselineData?.objectCategory == "Baseline"
          const shouldDisable = isBaseline && (_objectPropertyKey === 'objectName' || _objectPropertyKey === 'objectVersion' || _objectPropertyKey === 'objectProperties')
          return (
            <ArgosSelect
              disabled={shouldDisable || (cell.row.original.objectPropertyKey == "objectVersion" &&
                  (props?.baselineData.objectName == "" || props?.baselineData.objectCategory == "Custom"))
                || (cell.row.original.objectPropertyKey == "objectName" &&
                  props?.baselineData.objectCategory == "Custom") ||
                (cell.row.original.objectPropertyKey == "objectProperties" &&
                  props?.baselineData?.objectCategory === "Custom")
              }
              value={dropdownValue ?? ""} // This can't be undefined otherwise it will break component (it will be stuck blank)
              onChange={(e: any) => {
                if (cell.row.original.objectPropertyKey === 'temperature') {
                  const numberValue = Number(e.target.value)
                  props?.handleTempChange(numberValue)
                } else if (cell.row.original.objectPropertyKey == "objectCategory") {
                  props?.handleObjectCategoryChange(e)
                } else if (cell.row.original.objectPropertyKey == "objectName") {
                  props?.handleObjectNameChange(e)
                } else if (cell.row.original.objectPropertyKey == "objectProperties") {
                  props?.handleProfileChange(e)
                } else {
                  props?.handleObjectVersionChange(e)
                }
              }}
            >
              {menuItems.map((item: any) => <MenuItem value={String(item)}>{item} </MenuItem>)}
            </ArgosSelect>)
        } else {
          return <div> {cell.getValue() as any} </div>
        }
      }
    },
    {
      header: "Unit",
      accessorKey: "unit",
      enableEditing: false,
    },
  ]
  return (
    <div style={{padding: "5px"}}>
      <ArgosTableSimple
        columns={objectPropertiesRowColumns}
        data={tableData}

        editDisplayMode="cell"
        enableEditing
        enableBottomToolbar={false}
        initialState={{density: 'compact'}}
      />
    </div>)
}
export default ObjectProperties;

/**
 * Groups objects by their type, name, version, and profile.
 *
 * @param {any[]} objects - The list of objects to group.
 * @returns {object} The grouped objects.
 */
export function groupObjectsByTypeNameVersionProfile(objects: any[]) {
  const groupedObjects: any = {};

  if (!objects) {
    return groupedObjects;
  }

  objects.forEach((obj) => {
    const {type, latestVersion} = obj;

    if (type === 'Potentially Moving Object' || type === 'Miscellaneous') {
      const parameters = obj.parameters;
      const name = obj.objectName
      if (parameters == null || name == null) {
        return;
      }

      if (!groupedObjects[type]) {
        groupedObjects[type] = {};
      }

      if (!groupedObjects[type][name]) {
        groupedObjects[type][name] = {};
      }

      if (!groupedObjects[type][name][latestVersion]) {
        groupedObjects[type][name][latestVersion] = {};
      }

      Object.keys(parameters).forEach((profile) => {
        if (profile === 'general') {
          return;
        }
        const profileData = parameters[profile];

        if (!groupedObjects[type][name][latestVersion][profile]) {
          groupedObjects[type][name][latestVersion][profile] = {};
        }

        groupedObjects[type][name][latestVersion][profile] = profileData;
      });
    } else if (type === 'Sign') {
      const name = obj.signName

      if (!groupedObjects[type]) {
        groupedObjects[type] = {};
      }

      if (!groupedObjects[type][name]) {
        groupedObjects[type][name] = {};
      }

      if (!groupedObjects[type][name][latestVersion]) {
        groupedObjects[type][name][latestVersion] = {};
      }

      // Loop through signVariants and add each variant with key being the name signSizeVariant
      const signVariants = obj.signVariants;
      signVariants.forEach((signVariant: any) => {
        const signVariantName = signVariant.signSizeVariant;
        if (!groupedObjects[type][name][latestVersion][signVariantName]) {
          groupedObjects[type][name][latestVersion][signVariantName] = {};
        }
        groupedObjects[type][name][latestVersion][signVariantName] = signVariant;
      });
    }
  });

  return groupedObjects;
}

/**
 * Finds the object and variant in a single object by variant ID.
 *
 * @param {any} obj - The object to search.
 * @param {string} variantId - The variant ID to search for.
 * @returns {object|null} The object and variant name if found, otherwise null.
 */
export function findVariantInObject(obj: any, variantId: string) {
  const {type, parameters} = obj;

  if (type === 'Potentially Moving Object' || type === 'Miscellaneous') {
    for (const profile in parameters) {
      if (profile === 'general') continue;

      const profileData = parameters[profile];
      if (profileData.id === variantId) {
        const objectCategory = type
        const objectName = obj?.objectName
        const objectVersion = obj?.latestVersion
        const profileNameKey = profile
        const objectVariant = MAP_OBJECT_PROFILE_KEY_TO_PROFILE_NAME[profileNameKey ?? '']

        const filteredVariantData = {
          objectCategory,
          objectName,
          objectVersion,
          objectVariant,
          variantKey: profileNameKey,
          orientationWidth: profileData.orientationWidth,
          orientationHeight: profileData.orientationHeight,
        }
        return {object: obj, variantName: profile, filteredVariantData};
      }
    }
  } else if (type === 'Sign') {

    const signVariants = obj.signVariants;
    for (const signVariant of signVariants) {
      if (signVariant.id === variantId) {
        const objectCategory = type
        const objectName = obj?.signName
        const objectVersion = obj?.latestVersion
        const profileNameKey = signVariant.signSizeVariant
        const objectVariant = profileNameKey

        const filteredVariantData = {
          objectCategory,
          objectName,
          objectVersion,
          objectVariant,
          variantKey: profileNameKey,
          orientationWidth: signVariant.width,
          orientationHeight: signVariant.height,
        }
        return {object: obj, variantName: signVariant.signSizeVariant, filteredVariantData};
      }
    }
  }
  return null;
}

/**
 * Finds the object and variant in a list of objects by variant ID.
 *
 * @param {any[]} objects - The list of objects to search.
 * @param {string} variantId - The variant ID to search for.
 * @returns {object|null} The object and variant name if found, otherwise null.
 */
export function findObjectByVariantId(objects: any[], variantId: string) {
  for (const obj of objects) {
    const result = findVariantInObject(obj, variantId);
    if (result) {
      return result;
    }
  }
  return null;
}